import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Row, Col, Pagination, PaginationItem, PaginationLink } from "reactstrap";
import { ChevronUp, ChevronDown } from 'lucide-react';
import axios from "axios";
import WebService from "../../../services/webservice";
import { AesEncrypt, AesDecrypt } from "../../../services/crypto";
import { ComponentLoader } from '../common/Loader';
import img from '../../../images/no-data.svg';
import '../ProfileQuestions/ProfileQuestions.css'; // Reuse CSS layout styling if applicable
import './PeriodTrackingDashboard.css';

const CustomSelect = ({ value, options, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedOption = options.find(opt => opt.value === value) || options[0];

    return (
        <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
            <div 
                onClick={() => setIsOpen(!isOpen)}
                className="shadow-sm"
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '0 12px', height: '34px', background: '#fff', 
                    borderRadius: '20px', border: 'none',
                    fontSize: '12px', cursor: 'pointer', color: '#475569'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: selectedOption?.color || '#cbd5e1', marginRight: '8px', flexShrink: 0 }}></span>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500 }}>{selectedOption?.label}</span>
                </div>
                <i className={`fa fa-chevron-${isOpen ? 'up' : 'down'}`} style={{ fontSize: '10px', color: '#94a3b8', marginLeft: '6px' }}></i>
            </div>
            {isOpen && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, width: '100%', 
                    background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', 
                    marginTop: '6px', zIndex: 100, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                    maxHeight: '220px', overflowY: 'auto', padding: '6px'
                }}>
                    {options.map((opt, idx) => (
                        <div 
                            key={idx}
                            onClick={() => { onChange(opt.value); setIsOpen(false); }}
                            style={{
                                padding: '8px 12px', display: 'flex', alignItems: 'center', 
                                cursor: 'pointer', fontSize: '12px', color: '#475569', fontWeight: 500,
                                borderRadius: '8px', backgroundColor: value === opt.value ? '#f1f5f9' : 'transparent',
                                transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = value === opt.value ? '#f1f5f9' : 'transparent'}
                        >
                            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: opt.color || '#cbd5e1', marginRight: '8px', flexShrink: 0 }}></span>
                            {opt.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const ageOptions = [
    { value: 'All Ages', label: 'All Ages', color: '#94a3b8' },
    { value: 'Below 18', label: 'Below 18', color: '#3b82f6' },
    { value: '18 - 24', label: '18 - 24', color: '#10b981' },
    { value: '25 - 34', label: '25 - 34', color: '#f59e0b' },
    { value: '35 - 44', label: '35 - 44', color: '#ef4444' },
    { value: '45+', label: '45+', color: '#8b5cf6' }
];

const cycleLengthOptions = [
    { value: 'All', label: 'All', color: '#94a3b8' },
    { value: 'Short Cycle (Below 21 Days)', label: 'Short (<21)', color: '#3b82f6' },
    { value: 'Normal Cycle (21 - 28 Days)', label: 'Normal (21-28)', color: '#10b981' },
    { value: 'Long Cycle (29 - 35 Days)', label: 'Long (29-35)', color: '#f59e0b' },
    { value: 'Very Long Cycle (Above 35 Days)', label: 'Very Long (>35)', color: '#ef4444' }
];

const periodLengthOptions = [
    { value: 'All', label: 'All', color: '#94a3b8' },
    { value: 'Short (1 - 2 Days)', label: 'Short (1-2)', color: '#3b82f6' },
    { value: 'Normal (3 - 7 Days)', label: 'Normal (3-7)', color: '#10b981' },
    { value: 'Long (8+ Days)', label: 'Long (8+)', color: '#ef4444' }
];

const regularityOptions = [
    { value: 'All', label: 'All', color: '#94a3b8' },
    { value: 'Regular', label: 'Regular', color: '#10b981' },
    { value: 'Irregular', label: 'Irregular', color: '#f59e0b' }
];

const PeriodProfiles = () => {
    const navigate = useNavigate();
    
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(0);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    
    const [filters, setFilters] = useState({
        userId: '',
        ageCategory: 'All Ages',
        cycleLength: 'All',
        periodLength: 'All',
        healthConditions: '',
        regularity: 'All'
    });
    
    const isFetching = useRef(false);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchData(itemsPerPage, currentPage, filters);
        }, 500); // 500ms debounce for typing
        return () => clearTimeout(timeoutId);
    }, [currentPage, itemsPerPage, filters]);

    const fetchData = async (limit, page, currentFilters) => {
        if (isFetching.current) return;
        
        setIsLoading(true);
        isFetching.current = true;

        try {
            const payload = {
                limit: limit,
                page: page,
                filters: currentFilters
            };

            const encryptedPayload = AesEncrypt(payload);
            const response = await axios.post(`${WebService}api/period-profiles-list`, {
                EncData: encryptedPayload,
            });

            if (response.data && response.data.status) {
                const decrypted = AesDecrypt(response.data.data);
                
                // Calculate age dynamically
                const processedRecords = decrypted.records.map(record => {
                    let calculatedAge = "N/A";
                    if (record.user && record.user.dob) {
                        const dobParts = record.user.dob.split('-');
                        if (dobParts.length === 3) {
                            // "04-04-2001" -> dd-mm-yyyy
                            const day = parseInt(dobParts[0], 10);
                            const month = parseInt(dobParts[1], 10) - 1;
                            const year = parseInt(dobParts[2], 10);
                            
                            const dobDate = new Date(year, month, day);
                            if (!isNaN(dobDate.getTime())) {
                                const ageDifMs = Date.now() - dobDate.getTime();
                                const ageDate = new Date(ageDifMs); 
                                calculatedAge = Math.abs(ageDate.getUTCFullYear() - 1970);
                            }
                        }
                    }
                    return { ...record, calculatedAge };
                });

                setData(processedRecords);
                setTotalCount(decrypted.totalCount);
            } else {
                setData([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error("Error fetching period profiles data:", error);
            setData([]);
            setTotalCount(0);
        } finally {
            setIsLoading(false);
            isFetching.current = false;
        }
    };

    const handlePageChange = (page) => {
        if (page > 0 && page <= Math.ceil(totalCount / itemsPerPage)) {
            setCurrentPage(page);
        }
    };

    const handleItemsPerPageChange = (e) => {
        const val = Number(e.target.value);
        setItemsPerPage(val);
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalCount);

    const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });

    const handleSort = (field) => {
        let direction = 'asc';
        if (sortConfig.key === field && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key: field, direction });
    };

    const sortedData = useMemo(() => {
        if (!data) return [];
        let sortableItems = [...data];
        if (sortConfig.key) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                aValue = parseInt(aValue) || 0;
                bValue = parseInt(bValue) || 0;

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [data, sortConfig]);

    const renderSortHeader = (label, field) => (
        <th style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>{label}</span>
                <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: 'pointer', gap: '4px' }}>
                    <ChevronUp size={16} strokeWidth={3} color={sortConfig.key === field && sortConfig.direction === 'asc' ? '#0c0c0cff' : '#757373ff'} onClick={() => handleSort(field)} />
                    <ChevronDown size={16} strokeWidth={3} color={sortConfig.key === field && sortConfig.direction === 'desc' ? '#0c0c0cff' : '#757373ff'} onClick={() => handleSort(field)} />
                </span>
            </div>
        </th>
    );

    return (
        <div className="pq-list-container">
            <div className="d-flex align-items-center mb-5" style={{ position: 'relative' }}>
                <Button color="primary" className="btn-sm" style={{ width: 'auto', position: 'absolute', left: 0, zIndex: 1 }} onClick={() => navigate(-1)}>
                    <i className="fa fa-arrow-left me-2"></i> Back
                </Button>
                <h4 className="mb-0 w-100 text-center" style={{ fontSize: '18px', fontWeight: '600', color: '#334155' }}>Period Profiles</h4>
                <div style={{ position: 'absolute', right: 0, zIndex: 1 }}>
                    <span className="badge shadow-sm" style={{ backgroundColor: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', padding: '6px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center' }}>
                        Total : <span style={{ marginLeft: '4px', fontSize: '14px', fontWeight: '700' }}>{totalCount}</span>
                    </span>
                </div>
            </div>

            <div className="period-profiles-modern-filters mb-5 p-3 d-flex flex-wrap align-items-end justify-content-between gap-3" style={{ background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ flex: '0 0 130px' }}>
                    <label className="form-label mb-1" style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>User ID</label>
                    <Input 
                        className="pp-filter-input shadow-sm border-0"
                        type="text" 
                        placeholder="Search User ID" 
                        value={filters.userId}
                        onChange={(e) => { setFilters({ ...filters, userId: e.target.value }); setCurrentPage(1); }}
                        style={{ fontSize: '12px', padding: '0 12px', height: '34px', borderRadius: '20px', boxSizing: 'border-box' }}
                    />
                </div>
                <div style={{ flex: '0 0 130px' }}>
                    <label className="form-label mb-1" style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Age Category</label>
                    <CustomSelect 
                        value={filters.ageCategory} 
                        options={ageOptions} 
                        onChange={(val) => { setFilters({ ...filters, ageCategory: val }); setCurrentPage(1); }} 
                    />
                </div>
                <div style={{ flex: '0 0 140px' }}>
                    <label className="form-label mb-1" style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Cycle Length</label>
                    <CustomSelect 
                        value={filters.cycleLength} 
                        options={cycleLengthOptions} 
                        onChange={(val) => { setFilters({ ...filters, cycleLength: val }); setCurrentPage(1); }} 
                    />
                </div>
                <div style={{ flex: '0 0 140px' }}>
                    <label className="form-label mb-1" style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Period Length</label>
                    <CustomSelect 
                        value={filters.periodLength} 
                        options={periodLengthOptions} 
                        onChange={(val) => { setFilters({ ...filters, periodLength: val }); setCurrentPage(1); }} 
                    />
                </div>
                <div style={{ flex: '0 0 140px' }}>
                    <label className="form-label mb-1" style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Health Conditions</label>
                    <Input 
                        className="pp-filter-input shadow-sm border-0"
                        type="text" 
                        placeholder="Search Condition" 
                        value={filters.healthConditions}
                        onChange={(e) => { setFilters({ ...filters, healthConditions: e.target.value }); setCurrentPage(1); }}
                        style={{ fontSize: '12px', padding: '0 12px', height: '34px', borderRadius: '20px', boxSizing: 'border-box' }}
                    />
                </div>
                <div style={{ flex: '0 0 120px' }}>
                    <label className="form-label mb-1" style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Regularity</label>
                    <CustomSelect 
                        value={filters.regularity} 
                        options={regularityOptions} 
                        onChange={(val) => { setFilters({ ...filters, regularity: val }); setCurrentPage(1); }} 
                    />
                </div>
                <div style={{ flex: '0 0 auto' }}>
                    <button 
                        className="pp-filter-clear-btn-custom shadow-sm"
                        onClick={() => {
                            setFilters({
                                userId: '',
                                ageCategory: 'All Ages',
                                cycleLength: 'All',
                                periodLength: 'All',
                                healthConditions: '',
                                regularity: 'All'
                            });
                            setCurrentPage(1);
                        }}
                        style={{ 
                            fontSize: '12px', padding: '0 16px', height: '34px', 
                            display: 'flex', alignItems: 'center', borderRadius: '20px', 
                            fontWeight: 600, background: '#fff', border: '1px solid #f43f5e', 
                            color: '#000', cursor: 'pointer'
                        }}
                    >
                        <i className="fa fa-times me-1" style={{ color: '#f43f5e' }}></i> Clear
                    </button>
                </div>
            </div>

            {/* Table wrapper layout identical to ProfileQuestions */}
            <div className="pq-table-wrapper mt-4">
                <div className="pq-table-scroll">
                    <table className="pq-table">
                        <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                            <tr>
                                <th style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>S.No</th>
                                <th style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>User ID</th>
                                {renderSortHeader('Age', 'calculatedAge')}
                                {renderSortHeader('Cycle Length', 'cycleLength')}
                                {renderSortHeader('Period Length', 'periodLength')}
                                <th style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>Health Conditions</th>
                                <th style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>Regularity</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan="7" style={{ padding: '80px 0', textAlign: 'center' }}>
                                        <ComponentLoader color="#4f46e5" size="40px" />
                                    </td>
                                </tr>
                            ) : sortedData.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ padding: '80px 0', textAlign: 'center' }}>
                                        <img src={img} alt="No data" style={{ width: '250px', marginBottom: '20px' }} />
                                        <h4 style={{ color: '#64748b' }}>No matching records found.</h4>
                                    </td>
                                </tr>
                            ) : (
                                sortedData.map((item, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <span style={{ fontWeight: 600, color: '#64748b' }}>{startItem + idx}</span>
                                        </td>
                                        <td className="pq-title-cell">
                                            <div className="d-flex align-items-center">
                                                <span>{item.userId || 'N/A'}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="badge badge-light">{item.calculatedAge}</span>
                                        </td>
                                        <td>
                                            <span style={{ fontWeight: 500, color: '#334155' }}>{item.cycleLength || 'N/A'}</span>
                                        </td>
                                        <td>
                                            <span style={{ fontWeight: 500, color: '#334155' }}>{item.periodLength || 'N/A'}</span>
                                        </td>
                                        <td>
                                            {item.healthConditions && item.healthConditions.length > 0 ? (
                                                item.healthConditions.map((hc, i) => (
                                                    <span key={i} className="badge me-1 mb-1" style={{ backgroundColor: 'rgba(13, 202, 240, 0.1)', color: '#0dcaf0', border: 'none' }}>
                                                        {hc ? hc.replace(/_/g, ' ').charAt(0).toUpperCase() + hc.replace(/_/g, ' ').slice(1) : ''}
                                                    </span>
                                                ))
                                            ) : (
                                                <span style={{ color: '#94a3b8' }}>None</span>
                                            )}
                                        </td>
                                        <td>
                                            {item.regularity ? (
                                                <span className="badge" style={{
                                                    backgroundColor: item.regularity.toLowerCase() === 'regular' ? 'rgba(25, 135, 84, 0.1)' : 'rgba(255, 193, 7, 0.1)',
                                                    color: item.regularity.toLowerCase() === 'regular' ? '#198754' : '#ffc107',
                                                    border: 'none'
                                                }}>
                                                    {item.regularity}
                                                </span>
                                            ) : (
                                                <span style={{ color: '#94a3b8' }}>N/A</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Bottom Pagination identical to FeatureList */}
            {!isLoading && totalCount > 0 && (
                <div className="pq-pagination-container d-flex justify-content-between align-items-center p-3 border-top">
                    <div className="d-flex align-items-center">
                        <span className="pq-pagination-text me-2">
                            Showing {startItem} – {endItem} of {totalCount} entries. Per page: 
                        </span>
                        <Input 
                            type="select" 
                            value={itemsPerPage} 
                            onChange={handleItemsPerPageChange} 
                            className="pq-filter-input" 
                            style={{ width: '70px', display: 'inline-block', height: '32px !important', padding: '0 10px !important', margin: 0 }}
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </Input>
                    </div>

                    <Pagination size="sm" className="mb-0">
                        <PaginationItem className="pq-page-item" disabled={currentPage === 1}>
                            <PaginationLink className="pq-page-link" previous onClick={() => handlePageChange(currentPage - 1)} />
                        </PaginationItem>

                        {[...Array(totalPages)].map((_, i) => {
                            if (i === 0 || i === totalPages - 1 || (i >= currentPage - 2 && i <= currentPage)) {
                                return (
                                    <PaginationItem className="pq-page-item" active={i + 1 === currentPage} key={i}>
                                        <PaginationLink className="pq-page-link" onClick={() => handlePageChange(i + 1)}>
                                            {i + 1}
                                        </PaginationLink>
                                    </PaginationItem>
                                );
                            }
                            if (i === 1 && currentPage > 3) return <PaginationItem className="pq-page-item" disabled key="start-ellipsis"><PaginationLink className="pq-page-link">...</PaginationLink></PaginationItem>;
                            if (i === totalPages - 2 && currentPage < totalPages - 2) return <PaginationItem className="pq-page-item" disabled key="end-ellipsis"><PaginationLink className="pq-page-link">...</PaginationLink></PaginationItem>;
                            return null;
                        })}

                        <PaginationItem className="pq-page-item" disabled={currentPage === totalPages || totalPages === 0}>
                            <PaginationLink className="pq-page-link" next onClick={() => handlePageChange(currentPage + 1)} />
                        </PaginationItem>
                    </Pagination>
                </div>
            )}
        </div>
    );
};

export default PeriodProfiles;
